import { useList } from '@refinedev/core'
import { Button, Card, Select, Space, Spin, Table, Tag, Tooltip, Typography } from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  SearchOutlined
} from '@ant-design/icons'
import { useState } from 'react'

const { Text } = Typography

/**
 * 项目级激活日志（F8）
 *
 * 通过 useList 调用 GET /api/admin/{projectCode}/activations
 * 顶部筛选：action / success
 * 表格列：时间 / action（彩色 Tag） / 序列号(licenseId) / 设备(deviceId) /
 *        成功(✓/✗) / 错误码 / IP
 */

/** 激活动作类型 */
type ActivationAction = 'issue' | 'activate' | 'verify' | 'unbind' | 'revoke' | 'rebind'

/** 激活日志实体 */
interface Activation {
  id: string
  action: ActivationAction | string
  success: boolean
  licenseId: string | null
  serial: string | null
  deviceId: string | null
  deviceMachineCode: string | null
  errorCode?: string | null
  clientIp?: string | null
  userAgent?: string | null
  createdAt: string
}

/** action → Tag 颜色映射 */
const ACTION_COLOR: Record<string, string> = {
  issue: 'blue',
  activate: 'green',
  verify: 'default',
  rebind: 'orange',
  unbind: 'gold',
  revoke: 'red'
}

const monoStyle: React.CSSProperties = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 12
}

/** 格式化时间 */
function fmt(iso?: string | null): string {
  if (!iso) return '-'
  try {
    return new Date(iso).toLocaleString('zh-CN')
  } catch {
    return iso
  }
}

export function ActivationsList({ projectCode }: { projectCode: string }) {
  const [filters, setFilters] = useState<{
    action?: string
    success?: boolean
  }>({})
  const [tempAction, setTempAction] = useState<string | undefined>()
  const [tempSuccess, setTempSuccess] = useState<boolean | undefined>()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const listFilters = [
    ...(filters.action ? [{ field: 'action', operator: 'eq' as const, value: filters.action }] : []),
    ...(filters.success !== undefined
      ? [{ field: 'success', operator: 'eq' as const, value: filters.success }]
      : [])
  ]

  const { data, isLoading, isError, refetch } = useList<Activation>({
    resource: 'project-activations',
    meta: { projectCode },
    pagination: { current: page, pageSize },
    filters: listFilters
  })

  const activations = data?.data ?? []
  const total = data?.total ?? 0

  const handleSearch = () => {
    setFilters({ action: tempAction, success: tempSuccess })
    setPage(1)
  }

  const handleReset = () => {
    setTempAction(undefined)
    setTempSuccess(undefined)
    setFilters({})
    setPage(1)
  }

  if (isError) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Text type="secondary">加载失败</Text>
          <br />
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} style={{ marginTop: 12 }}>
            重试
          </Button>
        </div>
      </Card>
    )
  }

  return (
    <div>
      {/* 顶部筛选区 */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            allowClear
            placeholder="action 筛选"
            value={tempAction}
            onChange={(v) => setTempAction(v)}
            style={{ width: 160 }}
            options={[
              { value: 'issue', label: 'issue（发卡）' },
              { value: 'activate', label: 'activate（激活）' },
              { value: 'verify', label: 'verify（验证）' },
              { value: 'rebind', label: 'rebind（换机）' },
              { value: 'unbind', label: 'unbind（解绑）' },
              { value: 'revoke', label: 'revoke（吊销）' }
            ]}
          />
          <Select
            allowClear
            placeholder="成功/失败"
            value={tempSuccess}
            onChange={(v) => setTempSuccess(v)}
            style={{ width: 140 }}
            options={[
              { value: true, label: '成功' },
              { value: false, label: '失败' }
            ]}
          />
          <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
            搜索
          </Button>
          <Button onClick={handleReset}>重置</Button>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
            刷新
          </Button>
        </Space>
      </Card>

      {/* 日志表格 */}
      <Card size="small">
        <Spin spinning={isLoading}>
          <Table<Activation>
            rowKey="id"
            dataSource={activations}
            size="small"
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: ['10', '20', '50', '100'],
              showTotal: (t) => `共 ${t} 条`,
              onChange: (p, ps) => {
                setPage(p)
                setPageSize(ps)
              }
            }}
            columns={[
              {
                title: '时间',
                dataIndex: 'createdAt',
                width: 180,
                render: (v: string) => (
                  <span className="tabular-nums" style={{ fontSize: 12 }}>
                    {fmt(v)}
                  </span>
                )
              },
              {
                title: 'Action',
                dataIndex: 'action',
                width: 110,
                render: (v: string) =>
                  v ? <Tag color={ACTION_COLOR[v] ?? 'default'}>{v}</Tag> : '-'
              },
              {
                title: '序列号',
                dataIndex: 'serial',
                width: 280,
                render: (v: string | null) =>
                  v ? (
                    <code style={monoStyle}>{v}</code>
                  ) : (
                    <Text type="secondary">-</Text>
                  )
              },
              {
                title: '设备机器码',
                dataIndex: 'deviceMachineCode',
                width: 200,
                render: (v: string | null) =>
                  v ? (
                    <code style={monoStyle}>{v}</code>
                  ) : (
                    <Text type="secondary">-</Text>
                  )
              },
              {
                title: '成功',
                dataIndex: 'success',
                width: 80,
                render: (v: boolean) =>
                  v ? (
                    <CheckCircleOutlined style={{ color: '#10B981' }} />
                  ) : (
                    <CloseCircleOutlined style={{ color: '#F43F5E' }} />
                  )
              },
              {
                title: '错误码',
                dataIndex: 'errorCode',
                width: 160,
                render: (v?: string | null) =>
                  v ? (
                    <code style={{ ...monoStyle, color: '#F43F5E' }}>{v}</code>
                  ) : (
                    <Text type="secondary">-</Text>
                  )
              },
              {
                title: 'IP',
                dataIndex: 'clientIp',
                width: 140,
                render: (v?: string | null) =>
                  v ? <code style={monoStyle}>{v}</code> : <Text type="secondary">-</Text>
              },
              {
                title: 'UA',
                dataIndex: 'userAgent',
                width: 200,
                ellipsis: true,
                render: (v?: string | null) =>
                  v ? (
                    <Tooltip title={v}>
                      <Text style={{ fontSize: 12 }} ellipsis>
                        {v}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text type="secondary">-</Text>
                  )
              }
            ]}
          />
        </Spin>
      </Card>
    </div>
  )
}

